import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class VfkParserService {

  public getVfkType(vfkContent: string): 'vb' | 'parcel' {
    return vfkContent.includes('&DZVB') ? 'vb' : 'parcel';
  }

  public vfkToJson(vfkContent: string) {
    const type = this.getVfkType(vfkContent);

    switch (type) {
      case 'parcel':
        return this.vfkToJsonInt(vfkContent, [ 'HP', 'OP', 'SOBR', 'SBP', 'SBM' ]);
      case 'vb':
        return this.vfkToJsonInt(vfkContent, [ 'SOBR', 'SBP', 'ZVB' ]);
      default:
        throw new Error('Bad VFK');
    }
  }

  private vfkToJsonInt(vfkContent: string, allowedTypes: string[]) {
    const lines = vfkContent.split(/\r?\n/);
    let header;

    const result = {
      HP: [],
      OP: [],
      SOBR: [],
      SBP: [],
      SBM: [],
      ZVB: []
    };

    for (const line of lines) {
      const isHeader = line.startsWith('&B');
      const isData = line.startsWith('&D');

      if (!isHeader && !isData) {
        continue;
      }

      const row = line.split(';').map(this.parseValue);
      const type = (row[0] as string).replace('&D', '').replace('&B', '');

      if (!allowedTypes.includes(type)) {
        continue;
      }

      if (isHeader) {
        header = {};
        for (let i = 1; i < row.length; i++) {
          header[(row[i] as string).split(' ')[0]] = i;
        }
      } else if (header && isData) {
        const data = {};

        for (const [key, value] of Object.entries(header)) {

          data[key] = row[value as number];
        }

        result[type].push(data);
      }
    }

    return result;
  }

  public buildGeoJsonLayer(layer: string, vfkJson: any) {
    const result = {
      type: 'FeatureCollection',
      name: layer,
      crs: {
        type: 'name',
        properties: {
          name: 'urn:ogc:def:crs:EPSG::5514'
        }
      },
      features: []
    };

    if (layer === 'HP') {
      for (const hp of vfkJson.HP) {

        const sbp = vfkJson.SBP.filter(s => s.HP_ID === hp.ID);

        if (sbp.length < 2) {
          continue;
        }

        sbp.sort((a, b) => a.PORADOVE_CISLO_BODU - b.PORADOVE_CISLO_BODU);

        for (let i = 0; i < sbp.length - 1; i++) {
          const sobr1 = vfkJson.SOBR.find(so => so.ID === sbp[i].BP_ID);

          if (!sobr1) {
            continue;
          }

          const sobr2 = vfkJson.SOBR.find(so => so.ID === sbp[i + 1].BP_ID);

          if (!sobr2) {
            continue;
          }

          const point1 = [ -1 * sobr1.SOURADNICE_Y, -1 * sobr1.SOURADNICE_X ];
          const point2 = [ -1 * sobr2.SOURADNICE_Y, -1 * sobr2.SOURADNICE_X ];

          const geometry = {
            type: 'LineString',
            coordinates: [
              point1,
              point2
            ]
          };

          const feature = {
            type: 'Feature',
            properties: hp,
            geometry: geometry
          };

          result.features.push(feature);
        }
      }
    }

    if (layer === 'OP') {
      for (const op of vfkJson.OP) {
        let geometry;

        if (op.SOURADNICE_Y && op.SOURADNICE_X) {
          geometry = {
            type: 'Point',
            coordinates: [ -1 * op.SOURADNICE_Y, -1 * op.SOURADNICE_X ]
          };
        } else {
          const sbm = vfkJson.SBM.filter(s => s.OP_ID === op.ID);

          if (sbm.length !== 2) {
            continue;
          }

          geometry = {
            type: 'LineString',
            coordinates: [
              [ -1 * sbm[0].SOURADNICE_Y, -1 * sbm[0].SOURADNICE_X ],
              [ -1 * sbm[1].SOURADNICE_Y, -1 * sbm[1].SOURADNICE_X ],
            ]
          };
        }

        const feature = {
          type: 'Feature',
          properties: op,
          geometry: geometry
        };

        result.features.push(feature);
      }
    }

    if (layer === 'ZVB') {
      for (const zvb of vfkJson.ZVB) {
        const sbp = vfkJson.SBP.filter(s => s.ZVB_ID === zvb.ID);

        if (sbp.length < 2) {
          continue;
        }

        sbp.sort((a, b) => a.PORADOVE_CISLO_BODU - b.PORADOVE_CISLO_BODU);

        for (let i = 0; i < sbp.length - 1; i++) {
          const sobr1 = vfkJson.SOBR.find(so => so.ID === sbp[i].BP_ID);

          if (!sobr1) {
            continue;
          }

          const sobr2 = vfkJson.SOBR.find(so => so.ID === sbp[i + 1].BP_ID);

          if (!sobr2) {
            continue;
          }

          const point1 = [ -1 * sobr1.SOURADNICE_Y, -1 * sobr1.SOURADNICE_X ];
          const point2 = [ -1 * sobr2.SOURADNICE_Y, -1 * sobr2.SOURADNICE_X ];

          const geometry = {
            type: 'LineString',
            coordinates: [
              point1,
              point2
            ]
          };

          const feature = {
            type: 'Feature',
            properties: zvb,
            geometry: geometry
          };

          result.features.push(feature);
        }
      }
    }

    return result;
  }

  private parseValue(value: string): string | number | boolean | undefined {
    const cleaned = value.replace(/^"(.*)"$/, '$1');

    if (cleaned === '') {
      return undefined;
    }
    if (cleaned === 'true') {
      return true;
    }
    if (cleaned === 'false') {
      return false;
    }

    const num = Number(cleaned);
    if (!isNaN(num)) {
      return num;
    }

    return cleaned;
  }
}
